// Custom cookie consent — съхранение на избора + прилагане към Google Consent Mode v2
// и GTM dataLayer. Категории: necessary (винаги), analytics, marketing.

import { getSiteId } from '../config/sites';

// Ключът е scope-нат по сайт (мулти-сайт билд: travogue / babyvogue). В продукция
// двата бранда са на различни origin-и, но в dev вървят на един и същ localhost порт —
// без scope изборът за единия презаписваше другия.
export const CONSENT_STORAGE_KEY = `${getSiteId()}_cookie_consent`;
export const CONSENT_VERSION = 1;

// Колко дълго важи изборът, преди да попитаме отново. EDPB говори за „подходящи
// интервали"; националната практика клони към 12 месеца. Нарочно е константа, а не
// env/admin настройка: чете се синхронно в mount ефекта на CookieConsentProvider,
// преди първи paint — стойност от API би дошла след като банерът вече е решил дали
// да се покаже. Освен това е правен инвариант, свързан с CONSENT_VERSION.
const DAY_MS = 24 * 60 * 60 * 1000;
const DAYS_PER_MONTH = 30;

export const CONSENT_MAX_AGE_MONTHS = 12;
export const CONSENT_MAX_AGE_MS = CONSENT_MAX_AGE_MONTHS * DAYS_PER_MONTH * DAY_MS;

// Устройство с часовник няколко часа напред иначе би изтрило валидно съгласие.
export const CLOCK_SKEW_TOLERANCE_MS = DAY_MS;

/**
 * Кой Consent Mode сигнал от коя категория на банера зависи.
 *
 * Един източник на истината: ползва се и от `applyConsent()` тук, и от inline скрипта
 * в `<head>` (`src/lib/consentMode.js`), който издава същия ъпдейт синхронно, преди
 * контейнера. Два списъка, които се разминат, оставят сигнал заключен завинаги.
 *
 * Сигналите извън този обект (`functionality_storage`, `security_storage`) са в
 * категорията „необходими“ — те са granted в default-а и не се ъпдейтват.
 */
export const CONSENT_SIGNAL_CATEGORY = Object.freeze({
  ad_storage: 'marketing',
  ad_user_data: 'marketing',
  ad_personalization: 'marketing',
  analytics_storage: 'analytics',
  personalization_storage: 'marketing',
});

// Стойност при пълен отказ (само необходими).
export const REJECTED_CONSENT = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
});

// Стойност при пълно приемане.
export const ACCEPTED_CONSENT = Object.freeze({
  necessary: true,
  analytics: true,
  marketing: true,
});

// Бисквитки, които таговете в GTM контейнера реално пишат, по категория.
// Изтриват се best-effort при оттегляне/изтичане на съгласието.
// ВАЖНО: третостранните (MUID на .clarity.ms, fr на .facebook.com) НЕ са достъпни
// от JavaScript — тях ги спират само revoke таговете в GTM, не този код.
const CONSENT_COOKIE_PREFIXES = {
  analytics: ['_ga', '_gid', '_gat', '_clck', '_clsk'],
  marketing: ['_gcl_', '_gac', '_fbp', '_fbc'],
};

/** Домейните, на които може да е сет-ната бисквитка: host-only + родителските. */
function candidateCookieDomains() {
  const host = window.location.hostname;
  const domains = [null]; // host-only (без Domain атрибут)
  const parts = host.split('.');
  // Спираме на две части (example.com) — не пипаме публичния суфикс.
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`.${parts.slice(i).join('.')}`);
  }
  return domains;
}

/**
 * Изтрива първостранните бисквитки за дадена категория. Best-effort — виж бележката
 * при CONSENT_COOKIE_PREFIXES за какво НЕ може да направи.
 */
export function clearConsentCookies(category) {
  if (typeof document === 'undefined') {
    return;
  }
  const prefixes = CONSENT_COOKIE_PREFIXES[category];
  if (!prefixes) {
    return;
  }
  const names = document.cookie
    .split(';')
    .map((pair) => pair.split('=')[0].trim())
    .filter((name) => name && prefixes.some((prefix) => name.startsWith(prefix)));

  const domains = candidateCookieDomains();
  for (const name of names) {
    for (const domain of domains) {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; path=/${domainPart}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
    }
  }
}

/** Изтекло/невалидно съгласие — чистим и идентификаторите, които то е разрешило. */
function expireStoredConsent(parsed) {
  if (parsed.analytics) {
    clearConsentCookies('analytics');
  }
  if (parsed.marketing) {
    clearConsentCookies('marketing');
  }
  try {
    window.localStorage.removeItem(CONSENT_STORAGE_KEY);
  } catch {
    // localStorage недостъпен — няма какво да чистим.
  }
}

/**
 * Чете записания избор от localStorage. Връща null, ако още няма избор, форматът е
 * от друга версия или съгласието е изтекло (тогава се показва банерът).
 */
export function getStoredConsent() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    // Друга версия на категориите/текста → питаме отново.
    if (parsed.v !== CONSENT_VERSION) {
      expireStoredConsent(parsed);
      return null;
    }
    const ts = Number(parsed.ts);
    const age = Date.now() - ts;
    // Липсващ ts се третира като изтекъл (fail-closed).
    if (!Number.isFinite(ts) || age > CONSENT_MAX_AGE_MS || age < -CLOCK_SKEW_TOLERANCE_MS) {
      expireStoredConsent(parsed);
      return null;
    }
    return {
      necessary: true, // необходимите винаги са включени
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return null;
  }
}

/** Записва избора (с версия и timestamp — и двете се четат от getStoredConsent). */
export function saveConsent(consent) {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({
        necessary: true,
        analytics: Boolean(consent.analytics),
        marketing: Boolean(consent.marketing),
        v: CONSENT_VERSION,
        ts: Date.now(),
      }),
    );
  } catch {
    // localStorage недостъпен (частен режим) — просто не записваме.
  }
}

/**
 * Прилага избора към Google Consent Mode v2 (за GA4/Google) и към GTM dataLayer
 * (за да задействат тагове като Microsoft Clarity и Meta Pixel според съгласието).
 *
 * Сигналите идват от CONSENT_SIGNAL_CATEGORY — същия обект, от който се генерират
 * default-ите и синхронният ъпдейт в `<head>`, за да не може списъкът да се разминe.
 */
export function applyConsent(consent) {
  if (typeof window === 'undefined') {
    return;
  }

  // 1) Google Consent Mode v2 — GA4/Google Ads респектират тези стойности.
  if (typeof window.gtag === 'function') {
    const update = {};
    for (const [signal, category] of Object.entries(CONSENT_SIGNAL_CATEGORY)) {
      update[signal] = consent[category] ? 'granted' : 'denied';
    }
    window.gtag('consent', 'update', update);
  }

  // 2) GTM dataLayer събитие — за trigger-и на не-Google тагове (Clarity, Meta Pixel).
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cookie_consent_update',
    cookie_consent: {
      necessary: true,
      analytics: Boolean(consent.analytics),
      marketing: Boolean(consent.marketing),
    },
  });
}
